import { appAbility } from "./ability.service";
import { resetTenantScopedStores } from "@/domains/organization/services/tenant-scope-reset.service";
import posthog from "posthog-js";
import { signOut } from "firebase/auth";
import { auth } from "@/infrastructure/firebase";
export const AUTH_SESSION_STORAGE_KEYS = [
    "user",
    "org_id",
    "role_key",
    "auth_contexts",
    "memberships",
    "is_super_admin",
];
// Remove the pre-Firebase token during this release's migration window.
const LEGACY_AUTH_SESSION_STORAGE_KEYS = ["token"];
const resetHandlers = new Set();
export function clearAuthSessionStorage() {
    AUTH_SESSION_STORAGE_KEYS.forEach((key) => {
        localStorage.removeItem(key);
    });
    LEGACY_AUTH_SESSION_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}
export function registerAuthSessionResetHandler(handler) {
    resetHandlers.add(handler);
    return () => {
        resetHandlers.delete(handler);
    };
}
export function resetAuthSession() {
    clearAuthSessionStorage();
    resetTenantScopedStores();
    appAbility.update([]);
    resetHandlers.forEach((handler) => handler());
    posthog.reset();
}
export async function endAuthSession() {
    try {
        await signOut(auth);
    }
    finally {
        resetAuthSession();
    }
}
export function shouldAttachOrgContextHeader(config) {
    if (config.skipOrgContext ||
        config.orgContext === "global" ||
        config.orgContext === "none") {
        return false;
    }
    const path = getRequestPath(config);
    if (isOrgContextSuppressedPath(path)) {
        return false;
    }
    return config.orgContext === "tenant" || config.orgContext == null;
}
export async function applyAuthSessionHeaders(config) {
    config.headers ??= {};
    if (shouldAttachAuthorizationHeader(config) && auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
    }
    else {
        removeHeader(config.headers, "Authorization");
    }
    const orgId = localStorage.getItem("org_id");
    if (orgId && shouldAttachOrgContextHeader(config)) {
        config.headers["X-Org-Id"] = orgId;
    }
    else {
        removeHeader(config.headers, "X-Org-Id");
    }
    return config;
}
export function shouldAttachAuthorizationHeader(config) {
    return !isAuthorizationSuppressedPath(getRequestPath(config));
}
function getRequestPath(config) {
    const rawUrl = config.url ?? "";
    try {
        const fallbackOrigin = typeof window === "undefined" ? "http://localhost" : window.location.origin;
        const parsed = new URL(rawUrl, config.baseURL || fallbackOrigin);
        return normalizePath(parsed.pathname);
    }
    catch {
        return normalizePath(rawUrl.split("?")[0] ?? "");
    }
}
function normalizePath(path) {
    if (!path)
        return "/";
    return path.startsWith("/") ? path : `/${path}`;
}
function isOrgContextSuppressedPath(path) {
    if (path === "/api/v2/sessions" || path.startsWith("/api/v2/sessions/")) {
        return true;
    }
    if (isPasswordRecoveryPath(path))
        return true;
    return ["/auth", "/web", "/public", "/health", "/status"].some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}
function isAuthorizationSuppressedPath(path) {
    if (path === "/api/v2/sessions" || isPasswordRecoveryPath(path)) {
        return true;
    }
    return ["/web", "/public", "/health", "/status"].some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}
// Password recovery is anonymous: creation is public and completion is authorized solely by the
// `X-Access-Grant` capability header, so stale session credentials must never ride along.
function isPasswordRecoveryPath(path) {
    return (path === "/api/v2/password-recoveries" ||
        path.startsWith("/api/v2/password-recoveries/"));
}
function removeHeader(headers, name) {
    const axiosHeaders = headers;
    if (axiosHeaders.delete)
        axiosHeaders.delete(name);
    else
        delete headers[name];
}
